import { useRef, useEffect, useState } from "react";
import { v4 as uuid } from "uuid";
import { useTheme } from "./useTheme";
import { COMN_CODE } from "../constants";
import { useResourceContext } from "../features/context/ResourceContext";
import { useRecoilValue } from "recoil";
import { resourceState } from "../features/recoil";
import lodash from "lodash";
import { comnUtils } from "@/comn/utils";
import { getResourceVersion } from "../features/context/resourceContract";

export const useOptions_v2 = (
    props: any,
): {
    data: any[];
    options: { value: string; label: string }[];
    optionsItem: any;
    isLoading: any;
    getLoading: () => any;
    setOption: (...params: any) => any;
    findDataItem: (...params: any) => any;
    findOptionsItem: (...params: any) => any;
    getListItemKey: (...params: any) => any;
} => {
    const {
        componentType,
        area,
        comnCd,
        params,
        value,
        propsOptions,
        excludes,
        includes,
        filter,
        isOpen, // Only input code
    } = props;

    const $ = useRef({
        uuid: uuid(),
        key: null,
        updated: null,
        itemKey: null,
        pendingCount: 0,
        executeSingle: lodash.debounce(async (area: any, comnCd: any, params: any, value: any) => {
            try {
                setLoading(true);
                const result = await asyncSingle(area, undefined, params, value);
                if ($.current.key === getKey(area, comnCd, params, value)) {
                    $.current.uuid = uuid();
                    setResource(result);
                }
                setLoading(false);
            } catch (error) {
                // Error occurred
                $.current.key = null;
                console.log(error);
                setLoading(false);
            }
        }, 500),
    });

    const { theme } = useTheme();
    const {
        getKey,
        getIsAsync,
        makeOptions,
        makeOption,
        asyncNormal,
        asyncSingle,
        asyncMultiple,
        getResource,
        findMatchData,
    } = useResourceContext();
    const resourceAtom = useRecoilValue(resourceState);
    const [pendingCount, setPendingCount] = useState(0);
    const [resource, setResource] = useState(() => {
        return { data: [], options: [], count: 0, key: null, updated: null };
    });
    // Only input code
    const [optionsItem, setOptionsItem] = useState(null);

    const key = getKey(area, comnCd, params, value);
    const isLoading = pendingCount > 0;
    const isAsync = getIsAsync(area);
    const resourceType = propsOptions ? "property" : $.current.key ? "resource" : null;
    const isProperty = !!propsOptions;

    const setLoading = (status: any) => {
        if (status === true) {
            $.current.pendingCount += 1;
            setPendingCount((prev) => (prev += 1));
        }

        if (status === false) {
            $.current.pendingCount -= 1;
            setPendingCount((prev) => (prev -= 1));
        }
    };
    const setOption = (item: any) => {
        if (item) {
            $.current.itemKey = item.value;
            setOptionsItem(item);
        } else {
            $.current.itemKey = null;
            setOptionsItem(null);
        }
    };
    const getLoading = () => {
        return $.current.pendingCount > 0;
    };
    const getListItemKey = (item: any, index: any) => {
        return `${$.current.uuid};${JSON.stringify(item)};${index}`;
    };
    const findDataItem = (value: any) => {
        const code = COMN_CODE[area];
        return resource.data.find((item: any) => item[code.value] === value);
    };
    const findOptionsItem = (value: any) => {
        return resource.options.find((item: any) => item.value === value);
    };

    // Force update
    useEffect(() => {
        const key = getKey(area, comnCd);
        if (comnUtils.isEmpty(key)) return;
        const resourceVersion = getResourceVersion(resourceAtom[key]);
        if (resourceVersion === null || resourceVersion === $.current.updated) return;
        const newResource = getResource(area, comnCd);
        if (!newResource) return;
        $.current.uuid = uuid();
        $.current.updated = newResource.updated;
        setResource(newResource);
    }, [area, comnCd, resourceAtom]);

    // Get resource
    useEffect(() => {
        if (isProperty) return; // 리소스 타입이 property일 경우
        if (!area) return; // 공통코드를 위한 area가 없을 경우
        if (isAsync) return; // 비동기 조회가 필요한 코드 일 경우(대용량)
        if (componentType === "code" && isOpen !== true) return; // code 컴포넌트에서 리스트가 닫혀있는 경우
        if ($.current.key === getKey(area, comnCd)) return; // 리소스의 key가 같을 경우
        const execute = async () => {
            setLoading(true);
            try {
                const result = await asyncNormal(area, comnCd);
                $.current.uuid = uuid();
                $.current.key = result.key;
                $.current.updated = result.updated;
                setResource(result);
            } catch (error) {
                // Error occurred
                console.log(error);
            }
            setLoading(false);
        };
        execute();
    }, [area, comnCd, isProperty, componentType, isOpen, theme.lang]);

    // Get resource by value(Only input code)
    useEffect(() => {
        if (componentType !== "code") return; // code 컴포넌트가 아닐 경우
        if (!value) return; // value가 없는 경우
        if (isOpen === false) return; // code 컴포넌트에서 리스트가 닫혀있는 경우
        if (isProperty) return; // 리소스 타입이 property일 경우
        if (!area) return; // 공통코드를 위한 area가 없을 경우
        if (!isAsync) return; // 비동기 조회가 필요한 코드가 아닐 경우(대용량)
        if (value.length < COMN_CODE[area].path2MinLength) return; // value length
        if ($.current.key === key) return; // 리소스의 key가 같을 경우
        $.current.key = key;

        $.current.executeSingle(area, undefined, params, value);
        // const execute = async () => {
        //     try {
        //         setLoading(true);
        //         const result = await asyncSingle(area, undefined, params, value);
        //         if ($.current.key === key) {
        //             $.current.uuid = uuid();
        //             setResource(result);
        //         }
        //         setLoading(false);
        //     } catch (error) {
        //         // Error occurred
        //         $.current.key = null;
        //         console.log(error);
        //         setLoading(false);
        //     }
        // };
        // execute();
    }, [area, comnCd, JSON.stringify(params), value, isProperty, componentType, isOpen, theme.lang]);

    // Get resource item by value(Only input code)
    useEffect(() => {
        if (componentType !== "code") return; // code 컴포넌트가 아닐 경우
        if (isOpen) return; // code 컴포넌트에서 리스트가 열려있는 경우
        if ($.current.itemKey === value) return; // 리소스 항목의 key가 같을 경우
        $.current.itemKey = value;

        const execute = async () => {
            try {
                setLoading(true);
                const result = isAsync
                    ? await asyncMultiple(area, comnCd, undefined, value)
                    : await asyncNormal(area, comnCd, undefined, value);
                const optionsItem = result.options.find((item: any) => item.value === value) || null;
                if ($.current.itemKey === value) {
                    setOptionsItem(optionsItem);
                }
                setLoading(false);
            } catch (error) {
                console.log(error);
                setLoading(false);
            }
        };

        if (value) {
            execute();
        } else {
            setOptionsItem(null);
        }
    }, [area, comnCd, value, isAsync, componentType, isOpen]);

    let data: any[] = [];
    let options = [];
    // Switch data by resourceType
    switch (resourceType) {
        case "property":
            data = propsOptions;
            options = propsOptions;
            break;
        case "resource":
            data = resource.data;
            options = resource.options;
            break;
    }
    // Exclude items from options
    if (excludes) {
        if (Array.isArray(excludes)) {
            options = options.filter((item: any) => {
                return !excludes.includes(item.value);
            });
        }
        if (excludes instanceof Function) {
            options = data.filter((item: any) => {
                return !excludes(item);
            });
            if (resourceType === "resource") {
                options = makeOptions(area, data);
            }
        }
    }
    // Include items from options
    if (includes) {
        options = options.concat(includes);
    }

    // Filter items from options
    if (filter) {
        options = options.filter((item: any) => {
            return filter(item, findMatchData(area, item, data));
        });
    }

    // Filter items from options(Only input code, Normal)
    if (componentType === "code" && !isAsync && value) {
        options = options.filter((item: any) => {
            return (
                String(item.value)
                    .toUpperCase()
                    .includes(String(value || "").toUpperCase()) ||
                String(item.label)
                    .toUpperCase()
                    .includes(String(value || "").toUpperCase())
            );
        });
    }
    // Only input code
    if (componentType === "code" && !options.length) {
        options = [{ type: "NO_DATA" }];
    }

    return {
        data,
        options,
        optionsItem,
        isLoading,
        getLoading,
        setOption,
        findDataItem,
        findOptionsItem,
        getListItemKey,
    };
};
